import React from "react";
import { Banner } from "@jsq/ds/core";
import { isDiligenceNotConfirmed } from "#components/PayableWorkflow/utils";
import { PayeeStatus } from "#graphql/supergraphTypes";

export type PayeePendingBannerProps = {
  payeeStatus: PayeeStatus;
  payeeId: string;
  watchlistStatus?: Array<{ id: string; status: string }> | null;
};

/**
 * Consolidated banner that displays warnings for payee-related issues.
 * Shows different messages based on priority:
 * 1. Highest Priority: Watchlist screening not confirmed
 * 2. Lower Priority: Payee not approved
 *
 * Provides a clickable link to navigate to the payee when showing approval pending message.
 */
export const PayeePendingBanner: React.FC<PayeePendingBannerProps> = ({
  payeeStatus,
  payeeId,
  watchlistStatus,
}) => {
  // Priority 1: Check payee approval status
  if (payeeStatus !== "approved") {
    const description = (
      <>
        The{" "}
        <a
          href={`/canopy/treasury/payees/${payeeId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          payee
        </a>{" "}
        must be approved before you can approve this payment
      </>
    );

    return <Banner variant="warning" title="Payee pending review" description={description} />;
  }

  // Priority 2: Check watchlist screening status (highest priority)
  if (isDiligenceNotConfirmed(watchlistStatus)) {
    const description = (
      <>
        The{" "}
        <a
          href={`/canopy/treasury/payees/${payeeId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {"payee's"}
        </a>{" "}
        screening review case must be closed out before you can review this payment
      </>
    );
    return (
      <Banner
        variant="warning"
        title="Payee pending screening review"
        description={description}
      />
    );
  }

  // No banner needed - watchlist confirmed and payee approved
  return null;
};

export default PayeePendingBanner;
